import React, { useEffect, useMemo } from 'react'
import { useLocalStorage } from 'react-use'

import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import { type Metadata } from 'types/metadata'
import { ALL_ATTACK_TYPE_VALUES, getFormattedAttackType } from 'utils/metadata'

import { NO_TEAM } from './AuthContext'

export interface MetadataContext {
  metadata: Metadata
  metadataLoading: boolean
  metadataError: boolean
  getAttackName: (attackType: string) => string
}

export const METADATA_CONTEXT_DEFAULT_VALUES: MetadataContext = {
  metadata: [],
  metadataLoading: true,
  metadataError: false,
  getAttackName: (attackType: string) => attackType
}

export const MetadataContext = React.createContext<MetadataContext>(
  METADATA_CONTEXT_DEFAULT_VALUES
)

export function getAttackNameFromMetadata(
  attackType: string,
  metadata: Metadata
) {
  return (
    metadata.find((c) => c.command === attackType)?.name ??
    getFormattedAttackType(attackType)
  )
}

/**
 * Filters out metadata entries against the attack types that are supported and removes those that are not.
 *
 * @param metadata
 */
function getValidMetadata(metadata?: Metadata) {
  return (metadata ?? []).filter((m) =>
    ALL_ATTACK_TYPE_VALUES.includes(m.command)
  )
}

export const MetadataProvider: React.FC = ({ children }) => {
  const { currentTeamId } = useAuth()
  const [storedMetadata, setStoredMetadata] = useLocalStorage<Metadata>(
    'attack-metadata',
    []
  )

  const {
    data: metadata,
    isLoading: metadataLoading,
    hasError: metadataError
  } = useAgent(`/metadata?teamId=${currentTeamId}`, storedMetadata, {
    shouldCache: true,
    interval: 1000 * 60 * 60, // 1 hour,
    skipRequest: currentTeamId === NO_TEAM
  })

  const validMetadata = useMemo(() => getValidMetadata(metadata), [metadata])

  useEffect(() => {
    setStoredMetadata(validMetadata)
  }, [setStoredMetadata, validMetadata])

  const getAttackName = (attackType: string) => {
    return getAttackNameFromMetadata(attackType, validMetadata)
  }

  return (
    <MetadataContext.Provider
      value={{
        metadata: validMetadata,
        metadataLoading,
        metadataError,
        getAttackName
      }}
    >
      {children}
    </MetadataContext.Provider>
  )
}
